import React, { ChangeEvent, Component, createRef, ReactNode } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import moment from 'moment/moment';
import { Icon, Loading } from '@trlogic/ui-pack';
import _ from 'lodash';
import FormicaInput from 'components/custom/FormicaInput';
import EntityService from '../../../service/EntityService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { TransactionChatLog } from '../../../domain/case/CaseDomains';
import TransactionChatAttachmentItemComponent from '../case/modal/TransactionChatAttachmentItemComponent';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { RootState } from '../../../redux/store';
import { addPagePath, changePagePath } from '../../../redux/actions/PageActions';
import { EntityData } from '../../../domain/stream/transaction/EntityDomains';

let messagesEndRef: any = null;

class EntityChatContentComponent extends Component<Props, State> {
  public state: State = new State();

  private entityService: EntityService = ServiceContainer.getInstance().get(ServiceNames.ENTITY);
  private editInputRefs: { [key: string]: React.RefObject<HTMLInputElement> } = {};

  public render(): ReactNode {
    const { commentLoading, comment } = this.state;

    return (
      <div className={'w-full h-full px-5'}>
        <div className={'pb-10 flex-grow overflow-y-auto'}>
          {this.getAttachments()}
          <div ref={(el) => (messagesEndRef = el)} />
        </div>
        <div className={'flex gap-2 p-2 absolute bottom-0 left-0 w-full items-center justify-center'}>
          <FormicaInput value={comment} onChange={this.onChangeComment} onKeyPress={this.handleKeyPress} disabled={commentLoading} />
          <div
            className={`cursor-pointer bg-white border rounded w-[34px] h-[34px] min-h-[34px] min-w-[34px] shrink-0 flex items-center justify-center align-center text-f-purple `}
            onClick={this.sendComment}
          >
            {commentLoading ? <Loading /> : <Icon icon={'deploy'} size={20} color={'#284564'} />}
          </div>
        </div>
      </div>
    );
  }

  public componentDidMount(): void {
    this.scrollToBottom();
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (!_.isEqual(prevProps.chats, this.props.chats)) {
      this.scrollToBottom();
    }
    if (!_.isEqual(prevState.editComment, this.state.editComment)) {
      Object.keys(this.state.editComment).forEach((key) => {
        if (this.state.editComment[key] && this.editInputRefs[key]) {
          this.editInputRefs[key].current?.focus();
        }
      });
    }
  }

  private scrollToBottom = () => {
    messagesEndRef.scrollIntoView({ behavior: 'smooth' });
  };

  private getAttachments = () => {
    const { editComment } = this.state;
    const { chats, user } = this.props;
    return (
      chats &&
      chats.length > 0 &&
      chats.map((attachment: TransactionChatLog) => {
        if (attachment.type == 'COMMENT') {
          const isEditing = editComment[attachment.id];
          if (!this.editInputRefs[attachment.id]) {
            this.editInputRefs[attachment.id] = createRef();
          }

          return (
            <div
              key={attachment.id || attachment.timestamp}
              className={`flex rounded-[10px] w-2/3 border bg-white p-2 my-4 items-center gap-2 ${
                user.username === attachment.username ? 'ml-auto' : 'mr-auto'
              }`}
            >
              <div className={'flex flex-col flex-grow gap-y-1'}>
                <div className={'flex justify-between items-center'}>
                  <div className={'flex gap-x-1 items-center'}>
                    <div className={'flex justify-center align-center bg-[#D7E5FF] rounded-full w-[26px] h-[26px] min-h-[26px] min-w-[26px] shrink-0 '}>
                      <div className={'text-f-purple'}>
                        <Icon icon={'people'} size={16} color={'white'} />
                      </div>
                    </div>
                    <div className={'flex-grow text-xs text-[#6CA2FF]'}>{attachment.username}</div>
                  </div>
                  <div className={'text-[#333333] text-2xs font-extralight'}>{moment(attachment.timestamp).format('LLLL').valueOf()}</div>
                </div>
                <div className={'flex justify-between items-center gap-x-2'}>
                  <div className={'flex-grow overflow-auto text-[#4F4F4F] text-md'}>
                    {isEditing ? (
                      <input
                        defaultValue={attachment.content}
                        ref={this.editInputRefs[attachment.id]}
                        className={'w-full border border rounded px-1'}
                        onBlur={(event) => this.onBlurComment(event, attachment.id, attachment)}
                      />
                    ) : (
                      attachment.content
                    )}
                  </div>
                  <div>
                    <Icon icon={'edit'} size={16} color={'black'} className={'cursor-pointer'} onClick={() => this.editComment(attachment.id)} />
                  </div>
                </div>
              </div>
            </div>
          );
        } else if (attachment.type == 'ATTACHMENT') {
          return (
            <div
              key={attachment?.id}
              className={`flex rounded-[10px] w-2/3 border bg-white p-2 my-4 items-center gap-2 ${
                user.username === attachment.username ? 'ml-auto' : 'mr-auto'
              }`}
            >
              <div className={'flex flex-col flex-grow gap-y-1'}>
                <div className={'flex justify-between items-center'}>
                  <div className={'flex gap-x-1 items-center'}>
                    <div className={'flex justify-center align-center bg-[#D7E5FF] rounded-full w-[26px] h-[26px] min-h-[26px] min-w-[26px] shrink-0 '}>
                      <div className={'text-f-purple'}>
                        <Icon icon={'file'} size={16} color={'white'} />
                      </div>
                    </div>
                    <div className={'flex-grow text-xs text-[#6CA2FF]'}>
                      <span className={'text-[#6CA2FF] text-sm font-normal'}>{attachment.username}</span>
                    </div>
                  </div>
                  <div className={'text-[#333333] text-2xs font-extralight'}>{moment(attachment.timestamp).format('LLLL').valueOf()}</div>
                </div>
                <TransactionChatAttachmentItemComponent key={attachment?.id} attachment={attachment} />
              </div>
            </div>
          );
        }
      })
    );
  };

  private sendComment = async () => {
    const { comment } = this.state;
    const { domain, selectedEntity, updateEntity } = this.props;
    if (comment == undefined || comment.trim().length == 0 || domain == undefined || selectedEntity == undefined) return;
    this.setState({ commentLoading: true });
    try {
      await this.entityService.commentEntity(domain, selectedEntity.id, comment);
      updateEntity();
      this.setState({ comment: '' });
    } catch (e) {
      console.error(e);
    } finally {
      this.setState({ commentLoading: false });
    }
  };

  private onChangeComment = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ comment: event.target.value });
  };

  private editComment = (id: string) => {
    this.setState((prevState) => ({
      editComment: {
        ...prevState.editComment,
        [id]: !prevState.editComment[id],
      },
    }));
  };

  private onBlurComment = async (event: ChangeEvent<HTMLInputElement>, commentId: string, attachment: TransactionChatLog) => {
    const { domain, updateEntity } = this.props;
    if (domain == undefined) return;

    const oldContent = attachment.content;
    const newContent = event.target.value;
    attachment.content = newContent;

    try {
      await this.entityService.commentEditEntity(domain, newContent, commentId);
      updateEntity();
      this.setState((prevState) => ({
        editComment: {
          ...prevState.editComment,
          [commentId]: false,
        },
      }));
    } catch (e) {
      console.error(e);
      attachment.content = oldContent;
    }
  };

  private handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      this.sendComment();
    }
  };
}

class State {
  comment: string = '';
  commentLoading: boolean = false;
  editComment: { [key: string]: boolean } = {};
}

interface Props extends ReduxProps, FormicaProps {
  domain: string | undefined;
  selectedEntity: EntityData | undefined;
  chats: TransactionChatLog[];
  updateEntity: () => void;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
});
const mapDispatchToProps = { addPagePath, changePagePath };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(EntityChatContentComponent));
